export const MAX_EXPENSE_RECEIPT_FILE_BYTES = 4 * 1024 * 1024;
export const MAX_EXPENSE_RECEIPT_DATA_URL_LENGTH = 6_000_000;

const ALLOWED_MIME_TYPES = new Set([
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
  'image/gif',
  'image/heic',
  'image/heif',
  'application/pdf',
]);

export type ExpenseReceiptFileError = 'justificatifType' | 'justificatifTooLarge';

export function isPdfReceiptDataUrl(dataUrl: string): boolean {
  return /^data:application\/pdf/i.test(dataUrl);
}

function base64ToArrayBuffer(base64: string): ArrayBuffer {
  const binary = atob(base64.replace(/\s/g, ''));
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  const copy = new ArrayBuffer(bytes.byteLength);
  new Uint8Array(copy).set(bytes);
  return copy;
}

/** Chrome cannot display large `data:application/pdf` URLs; blob URLs work. */
export function openPdfReceiptInBrowser(dataUrl: string, fileName = 'justificatif.pdf'): void {
  if (typeof window === 'undefined') return;
  const match = /^data:([^;]+);base64,([\s\S]+)$/i.exec(dataUrl);
  if (!match) return;
  const blob = new Blob([base64ToArrayBuffer(match[2])], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const opened = window.open(url, '_blank');
  if (opened) return;
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export function isImageReceiptDataUrl(dataUrl: string): boolean {
  return /^data:image\//i.test(dataUrl);
}

export function mimeTypeFromFileName(name: string): string | null {
  const lower = name.toLowerCase();
  if (lower.endsWith('.pdf')) return 'application/pdf';
  if (lower.endsWith('.png')) return 'image/png';
  if (lower.endsWith('.webp')) return 'image/webp';
  if (lower.endsWith('.gif')) return 'image/gif';
  if (lower.endsWith('.heic')) return 'image/heic';
  if (lower.endsWith('.heif')) return 'image/heif';
  if (lower.endsWith('.jpg') || lower.endsWith('.jpeg')) return 'image/jpeg';
  return null;
}

export function isAllowedExpenseReceiptFile(file: File): boolean {
  const type = (file.type || '').toLowerCase();
  if (ALLOWED_MIME_TYPES.has(type) || type.startsWith('image/')) return true;
  return mimeTypeFromFileName(file.name) != null;
}

export function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error ?? new Error('read failed'));
    reader.readAsDataURL(file);
  });
}

export async function fileToExpenseReceiptDataUrl(
  file: File
): Promise<{ dataUrl: string; fileName: string } | { error: ExpenseReceiptFileError }> {
  if (!isAllowedExpenseReceiptFile(file)) {
    return { error: 'justificatifType' };
  }
  if (file.size > MAX_EXPENSE_RECEIPT_FILE_BYTES) {
    return { error: 'justificatifTooLarge' };
  }
  const dataUrl = await readFileAsDataUrl(file);
  if (!dataUrl || dataUrl.length > MAX_EXPENSE_RECEIPT_DATA_URL_LENGTH) {
    return { error: 'justificatifTooLarge' };
  }
  if (!isPdfReceiptDataUrl(dataUrl) && !isImageReceiptDataUrl(dataUrl)) {
    return { error: 'justificatifType' };
  }
  return { dataUrl, fileName: file.name };
}
